import { useEffect, useMemo, useState } from 'react';
import { Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { formatDate } from '@/lib/format.js';
import useDeal from '@/hooks/useDeal.js';
import { getAccountCommsTimeline } from '@/services/account.service.js';
import ComposeSheet from '@/components/comms/ComposeSheet.jsx';
import { recommendedForStage } from '@/components/comms/stageTemplates.js';
import Panel from './Panel.jsx';

// Spec §4.5: every send via the Comms Hub adds a new entry. Filter
// chips at top scope the view (All / Sent / Received / Calls /
// System). Compose CTA at the top opens the Comms Hub for this deal.
//
// Entry shape:
//   { id, occurred_at, actor, actor_kind?, summary, tag, source_type, source_id }
// actor is the server's word (utils/activity-actor.js, deals-door-1 T24); the
// panel only marks the two kinds that say the who was not recorded.
const NOT_RECORDED_KINDS = new Set(['sign_in_not_recorded', 'not_recorded']);

const TAG_VARIANT = {
  email: 'info',
  sms: 'info',
  call: 'success',
  system: 'outline',
  broker_note: 'warning',
  document: 'outline',
};

// Filter-chip → tag predicate. 'All' matches everything; 'Sent' picks
// up email/sms; 'Received' is reserved for inbound and currently
// matches nothing (inbound parsing is V1.5+ — spec §15 out of scope).
const FILTERS = [
  { id: 'all', label: 'All', match: () => true },
  { id: 'sent', label: 'Sent', match: (e) => e.tag === 'email' || e.tag === 'sms' },
  { id: 'received', label: 'Received', match: () => false },
  { id: 'calls', label: 'Calls', match: (e) => e.tag === 'call' },
  { id: 'system', label: 'System', match: (e) => e.tag === 'system' },
];

export default function CommsTimelinePanel({ timeline, dealId, accountId, onComposed }) {
  const [filterId, setFilterId] = useState('all');
  const [composeOpen, setComposeOpen] = useState(false);
  const { data: dealRes } = useDeal(dealId);
  const sessionToken = dealRes?.deal?.session_token || null;
  const crmStage = dealRes?.deal?.deal_stage || null;

  // §C3 — focused-deal view scopes Activity to THAT deal (the hangar deal was
  // showing the personal aircraft deal's PHF events). Account-level fallback
  // when no deal is focused or the scoped fetch fails. Re-runs when the
  // workspace timeline refreshes (post-compose) so the scoped view stays live.
  const [dealTimeline, setDealTimeline] = useState(null);
  useEffect(() => {
    let aborted = false;
    if (!dealId || !accountId) {
      setDealTimeline(null);
      return undefined;
    }
    getAccountCommsTimeline(accountId, dealId)
      .then((d) => {
        if (!aborted) setDealTimeline(d);
      })
      .catch(() => {
        if (!aborted) setDealTimeline(null);
      });
    return () => {
      aborted = true;
    };
  }, [dealId, accountId, timeline]);

  const effectiveTimeline = (dealId && dealTimeline) ? dealTimeline : timeline;
  const allEntries = useMemo(() => effectiveTimeline?.entries || [], [effectiveTimeline]);
  const filtered = useMemo(() => {
    const fn = FILTERS.find((f) => f.id === filterId)?.match || (() => true);
    return allEntries.filter(fn);
  }, [allEntries, filterId]);

  const recommended = recommendedForStage(crmStage);

  return (
    <Panel
      title="Activity"
      count={filtered.length}
      footer={
        effectiveTimeline?.has_more ? (
          <span className="text-text-dim">More activity available.</span>
        ) : null
      }
      empty={null}
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilterId(f.id)}
              className={`rounded-pill px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide transition ${
                filterId === f.id
                  ? 'bg-accent-soft text-accent'
                  : 'text-text-dim hover:bg-border/30 hover:text-text-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {/* Sessionless deals send the deal-keyed broker templates, so the
            Compose button stays enabled whenever a deal is in scope (the
            ComposeSheet falls back to the deal-keyed path on dealId). */}
        {dealId ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setComposeOpen(true)}
            className="h-7 gap-1.5 px-2 text-xs"
          >
            <Send size={12} strokeWidth={1.5} />
            Compose
          </Button>
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <p className="text-xs italic text-text-dim">No activity in this filter.</p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((e) => (
            <li
              key={e.id}
              className="flex items-start gap-3 border-l-2 border-border pl-3 hover:border-border-strong"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  {e.tag ? (
                    <Badge variant={TAG_VARIANT[e.tag] || 'outline'} className="text-[9px]">
                      {e.tag.replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                  ) : null}
                  <span className="text-xs text-text-dim">
                    {formatDate(e.occurred_at, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                  {e.actor ? (
                    NOT_RECORDED_KINDS.has(e.actor_kind) ? (
                      <span
                        className="text-xs italic text-text-dim"
                        data-actor-kind={e.actor_kind}
                        title="Who did this was not recorded"
                      >
                        · {e.actor}
                      </span>
                    ) : (
                      <span className="text-xs text-text-dim">· {e.actor}</span>
                    )
                  ) : null}
                </div>
                <div className="mt-0.5 text-sm text-text">{e.summary}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ComposeSheet
        sessionToken={sessionToken}
        dealId={dealId}
        open={composeOpen}
        onOpenChange={setComposeOpen}
        recommendedSlugs={recommended}
        onSent={onComposed}
      />
    </Panel>
  );
}

// Stage map lives in '@/components/comms/stageTemplates.js' so this file
// and DealActionBar stay in sync (2026-05-20 Band 0 polish).
